import clsx from 'clsx';
import { useEditorRef } from 'platejs/react';
import { useDropdown } from '../../hooks/useDropdown';
import { useSelectionFragmentProp } from '@platejs/utils/react';
import FormatLineSpacingIcon from '@mui/icons-material/FormatLineSpacing';
import ArrowDropDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded';
import textColorStyles from '../TextColorMenu/TextColorMenu.module.scss';
import toolbarStyles from '../PlateToolbar/PlateToolbar.module.scss';

const PRESETS: Array<{ value: number; label: string }> = [
    { value: 1, label: '1' },
    { value: 1.15, label: '1.15' },
    { value: 1.2, label: '1.2 (tight)' },
    { value: 1.5, label: '1.5' },
    { value: 1.75, label: '1.75' },
    { value: 2, label: '2' },
    { value: 2.5, label: '2.5' },
    { value: 3, label: '3' },
];

export function LineHeightMenu() {
    const editor = useEditorRef();
    const { open, setOpen, ref: wrapRef } = useDropdown();

    const current = (useSelectionFragmentProp as any)({
        defaultValue: 1.2,
        getProp: (node: any) => node?.lineHeight,
    }) as number | undefined;

    // Direct `setNodes` write — bypasses `@platejs/basic-styles`' setLineHeight
    // helper, which routes through Plate's nodeProps inject + getInjectMatch
    // ladder. That ladder was returning a match that didn't reliably write
    // the prop onto list blocks (paragraphs with listStyleType), so picking
    // a value in the menu was a no-op for numbered/bulleted items. Writing
    // `lineHeight` directly via setNodes lands the prop on every block in
    // the selection, regardless of whether it's also a list item.
    const pick = (value: number) => (e: React.MouseEvent) => {
        e.preventDefault();
        const ed: any = editor;
        const BLOCK_TARGETS = new Set(['p', 'h1', 'h2', 'h3', 'blockquote', 'callout', 'td', 'th']);
        ed.tf?.setNodes?.({ lineHeight: value }, { match: (n: any) => n && typeof n === 'object' && BLOCK_TARGETS.has(n.type) });
        setOpen(false);
    };

    return (
        <div className={textColorStyles.wrap} ref={wrapRef}>
            <button
                type="button"
                className={clsx(toolbarStyles.btn, open && toolbarStyles.btnActive)}
                onMouseDown={(e) => {
                    e.preventDefault();
                    setOpen((o) => !o);
                }}
                title="Line spacing"
                aria-label="Line spacing"
                aria-haspopup="true"
                aria-expanded={open}
            >
                <FormatLineSpacingIcon fontSize="small" />
                <ArrowDropDownRoundedIcon fontSize="small" style={{ marginLeft: -2 }} />
            </button>
            {open && (
                <div className={textColorStyles.popover} role="menu" aria-label="Pick line spacing" style={{ minWidth: 140 }}>
                    {PRESETS.map(({ value, label }) => (
                        <button
                            key={value}
                            type="button"
                            role="menuitem"
                            className={clsx(textColorStyles.clear, current === value && toolbarStyles.btnActive)}
                            onMouseDown={pick(value)}
                            title={`Line height ${label}`}
                            style={{ marginTop: 0, marginBottom: 2, textAlign: 'left', justifyContent: 'flex-start', paddingLeft: 10 }}
                        >
                            {label}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
